import { createAsyncThunk } from "@reduxjs/toolkit"
import wsClient from "src/wsClient"
import i18n from "src/i18n"

import {
  addChatMessage,
  addUserToLobby,
  addUserToTeam,
  changeUserRace,
  changeUserUnitIcon,
  leaveLobby,
  removeUserFromLobby,
  removeUserFromTeam,
  setLobbies,
  setRoundSeconds,
  setUserReady,
  switchUserLocation,
  unsetUserReady,
} from "src/store/lobby"
import { setGame } from "src/store/game"
import { addMessage } from "src/store/messages"
import { playerFacingError } from "src/utils/errorMessages"
import { playUiSound } from "src/utils/audio"
import { selectUserId, selectUsername } from "src/store/user"

/** Whether a lobby payload, as the server sends it, still holds `userId`'s seat. */
export const seatedIn = (lobby, userId) =>
  (lobby?.lobby_users ?? []).some((lobbyUser) => String(lobbyUser.user?.id) === String(userId))

export const thunk = createAsyncThunk("lobby/startWebSocket", async (data, { dispatch, getState, rejectWithValue }) => {
  try {
    // This is a thunk because we need to use the refreshToken from the store, and React doesn't support
    // accessing global store state in reducers.
    await wsClient.connect({
      key: "lobby",
      url: `lobby/${data.lobbyNumber}/`,
      onOpenCallback: [],
      onMessageCallback: [
        (message) => {
          if (message.action === "receive_chat_message") {
            // Somebody else typed (owner, 2026-09-20: "so we are aware"); one's own line, which
            // the server echoes back like everybody's, stays silent.
            if (message.username && message.username !== selectUsername(getState())) {
              playUiSound("chat")
            }
            dispatch(
              addChatMessage({
                message: message.message,
                userId: message.user_id,
                username: message.username,
                messageType: message.message_type,
              })
            )
          } else if (message.action === "user_joined_lobby") {
            dispatch(addUserToLobby({ user: message.user }))
            dispatch(
              addChatMessage({
                message: i18n.t("lobby.userJoined", { username: message.user.username }),
                messageType: "system",
              })
            )
          } else if (message.action === "user_left_lobby") {
            dispatch(removeUserFromLobby({ userId: message.user.id, host: message.host }))
            dispatch(
              addChatMessage({
                message: i18n.t("lobby.userLeft", { username: message.user.username }),
                messageType: "system",
              })
            )
          } else if (message.action === "user_removed_from_lobby") {
            dispatch(removeUserFromLobby({ userId: message.user.id, host: message.host }))
            dispatch(
              addChatMessage({
                message: i18n.t("lobby.userRemoved", { username: message.user.username }),
                messageType: "system",
              })
            )
          } else if (message.action === "removed_from_lobby") {
            // The host put this account out; the server closes the socket right after. Same exit
            // as a seat given up elsewhere: the slice goes, and `sentHome` makes the lobby route
            // take the player home (nothing else navigates; without it the room's address showed
            // a black screen and a reload joined the room again, owner, 2026-10-16).
            dispatch(addMessage({ text: i18n.t("lobby.removed"), type: "warning" }))
            dispatch(leaveLobby({ lobbyNumber: data.lobbyNumber, sentHome: true }))
            return
          } else if (message.action === "user_joined_team") {
            dispatch(addUserToTeam({ userId: message.user.id, team: message.team, unitIcon: message.unit_icon }))
          } else if (message.action === "user_left_team") {
            dispatch(removeUserFromTeam({ userId: message.user.id }))
          } else if (message.action === "round_seconds_set") {
            dispatch(setRoundSeconds(message.seconds))
          } else if (message.action === "user_changed_race") {
            dispatch(changeUserRace({ userId: message.user.id, race: message.race }))
          } else if (message.action === "user_changed_unit_icon") {
            dispatch(changeUserUnitIcon({ userId: message.user.id, unitIcon: message.unit_icon }))
          } else if (message.action === "user_switched_location") {
            dispatch(switchUserLocation({ userId: message.user.id, location: message.location }))
          } else if (message.action === "user_set_ready") {
            dispatch(setUserReady({ userId: message.user.id }))
          } else if (message.action === "user_unset_ready") {
            dispatch(unsetUserReady({ userId: message.user.id }))
          } else if (message.action === "lobby_refreshed") {
            /* The whole room, and since B2 (2026-09-20) it follows every action rather than only
               the ones no delta covers — a quick start, which is HTTP, or a bot's cells moving on
               somebody else's press. It is what the client trusts: the deltas above are immediate
               and lossy, and this corrects whichever of them was missed or applied twice.

               `ifNewer` is what stops it undoing itself. Two of these can be serialised after two
               commits and broadcast in either order, and the older one describes a room that no
               longer exists — a seat somebody has just taken, gone again. */
            if (!seatedIn(message.lobby, selectUserId(getState()))) {
              /* The room no longer has this account in it. That is another tab of the same
                 account giving the seat up — the installed app in the background, say, whose
                 socket the phone dropped — and the server tells every socket in the room,
                 this one included. Applied as a snapshot it was a room the lobby view could not
                 draw (`userLobby.team` on nobody: a black screen a few seconds into the lobby,
                 owner, 2026-09-24). Out is out: the socket goes, the slice goes, and the router
                 takes the player home. */
              dispatch(addMessage({ text: i18n.t("lobby.seatGone"), type: "warning" }))
              dispatch(leaveLobby({ lobbyNumber: message.lobby.number, sentHome: true }))
              return
            }
            dispatch(setLobbies({ lobbies: [message.lobby], ifNewer: true }))
          } else if (message.action === "user_started_game") {
            dispatch(setGame([message.game]))
            dispatch(leaveLobby({ lobbyNumber: message.lobby_number }))
          } else if (message.action === "error") {
            // There was no branch for this at all, so every refusal the lobby sent — no team, a
            // cell somebody else had taken, readying without your starting cells, starting a game
            // you do not host — arrived as nothing whatsoever. Requirement 16 fixed this for the
            // game and the lobby was out of its scope.
            dispatch(addMessage({ text: playerFacingError(message.message), type: "error" }))
            console.warn("[lobby ws] server rejected an action:", message.message)
          } else {
            console.warn("[lobby ws] unhandled action:", message.action)
          }
        },
      ],
      onErrorCallback: [],
      onCloseCallback: [],
    })
    return true
  } catch (error) {
    return rejectWithValue(i18n.t("lobby.wsError"))
  }
})

export const onSucceed = null

const thunkObject = { thunk, onSucceed }
export default thunkObject
